import type { YinshState } from "@cloudnest/yinsh-engine";
import {
  applyForfeit,
  applyMoveRing,
  applyPlaceMarker,
  applyPlaceRing,
  applyRemoveRing,
  applySelectRow,
  validateForfeit,
  validateMoveRing,
  validatePlaceMarker,
  validatePlaceRing,
  validateRemoveRing,
  validateSelectRow,
} from "@cloudnest/yinsh-engine";
import { toYinshReplayEvent, type YinshReplayEvent } from "./event-adapter";
import type { YinshReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free YINSH replay simulation. Mirrors the live store's `applyEvent`
 * (place_ring / place_marker / move_ring / take_turn / select_row /
 * remove_ring / forfeit) but validates each event before reducing so the
 * runner fails loudly on a malformed fixture. Imports only the engine — no
 * React, stores, auth, router, or WebSocket.
 */
export interface YinshReplaySnapshot {
  readonly index: number;
  readonly event: YinshReplayFixtureEvent;
  readonly replayEvent: YinshReplayEvent;
  readonly state: YinshState;
}

interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
}

function requireState(
  state: YinshState | null,
  eventType: YinshReplayEvent["eventType"],
): YinshState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function ensureValid(label: string, result: ValidationResult): void {
  if (!result.valid) {
    throw new Error(
      `${label} rejected: ${result.error ?? "Validation failed"}`,
    );
  }
}

function applyReplayEvent(
  state: YinshState | null,
  event: YinshReplayEvent,
): YinshState {
  switch (event.eventType) {
    case "init":
      return event.payload.state;

    case "place_ring": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "PLACE_RING" as const,
        player: event.payload.player,
        position: event.payload.position,
      };
      ensureValid("place_ring", validatePlaceRing(current, action));
      return applyPlaceRing(current, action);
    }

    case "place_marker": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "PLACE_MARKER" as const,
        player: event.payload.player,
        ringPosition: event.payload.ringPosition,
      };
      ensureValid("place_marker", validatePlaceMarker(current, action));
      return applyPlaceMarker(current, action);
    }

    case "move_ring": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "MOVE_RING" as const,
        player: event.payload.player,
        from: event.payload.from,
        to: event.payload.to,
      };
      ensureValid("move_ring", validateMoveRing(current, action));
      return applyMoveRing(current, action);
    }

    case "take_turn": {
      const current = requireState(state, event.eventType);
      const placeAction = {
        type: "PLACE_MARKER" as const,
        player: event.payload.player,
        ringPosition: event.payload.ringPosition,
      };
      ensureValid(
        "take_turn:place_marker",
        validatePlaceMarker(current, placeAction),
      );
      const afterPlace = applyPlaceMarker(current, placeAction);
      const moveAction = {
        type: "MOVE_RING" as const,
        player: event.payload.player,
        from: event.payload.ringPosition,
        to: event.payload.destination,
      };
      ensureValid(
        "take_turn:move_ring",
        validateMoveRing(afterPlace, moveAction),
      );
      return applyMoveRing(afterPlace, moveAction);
    }

    case "select_row": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "SELECT_ROW" as const,
        player: event.payload.player,
        positions: event.payload.positions,
      };
      ensureValid("select_row", validateSelectRow(current, action));
      return applySelectRow(current, action);
    }

    case "remove_ring": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "REMOVE_RING" as const,
        player: event.payload.player,
        position: event.payload.position,
      };
      ensureValid("remove_ring", validateRemoveRing(current, action));
      return applyRemoveRing(current, action);
    }

    case "forfeit": {
      const current = requireState(state, event.eventType);
      const action = {
        type: "FORFEIT" as const,
        player: event.payload.player,
      };
      ensureValid("forfeit", validateForfeit(current, action));
      return applyForfeit(current, action);
    }
  }
}

export function buildYinshReplaySnapshots(
  events: readonly YinshReplayFixtureEvent[],
): readonly YinshReplaySnapshot[] {
  const snapshots: YinshReplaySnapshot[] = [];
  let state: YinshState | null = null;

  for (const event of events) {
    const replayEvent = toYinshReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty YINSH event log");
  }

  return snapshots;
}
